import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  CircularProgress,
  Box,
  IconButton,
  TextField,
  Popover,
  Tooltip,
  Button,
  Stack,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";

export default function LazyTable({
  data,
  page,
  rowsPerPage,
  totalRecords,
  loading,
  columns,
  onPageChange,
  onRowsPerPageChange,
  onFilterChange,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeFilterCol, setActiveFilterCol] = useState(null);
  const [filters, setFilters] = useState({});
  const [tempFilters, setTempFilters] = useState({});

  // Open filter popup
  const handleFilterClick = (event, col) => {
    setAnchorEl(event.currentTarget);
    setActiveFilterCol(col.field);
    setTempFilters((prev) => ({
      ...prev,
      [col.field]: filters[col.field] || "",
    }));
  };

  const handleClose = () => {
    setAnchorEl(null);
    setActiveFilterCol(null);
  };

  const handleTempChange = (field, value) => {
    setTempFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleVerify = () => {
    const newFilters = {
      ...filters,
      [activeFilterCol]: tempFilters[activeFilterCol] || "",
    };
    setFilters(newFilters);
    if (onFilterChange) onFilterChange(newFilters);
    handleClose();
  };

  const handleClear = () => {
    const newFilters = { ...filters };
    delete newFilters[activeFilterCol];

    const newTemp = { ...tempFilters };
    delete newTemp[activeFilterCol];

    setFilters(newFilters);
    setTempFilters(newTemp);

    if (onFilterChange) onFilterChange(newFilters);
    handleClose();
  };

  return (
    <Paper sx={{ position: "relative" }} elevation={2}>
      {/* Loader Overlay */}
      {loading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(255, 255, 255, 0.6)",
            zIndex: 2,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      )}

      <TableContainer>
        <Table
          stickyHeader
          sx={{
            "& .MuiTableCell-root": {
              paddingTop: "8px",
              paddingBottom: "8px",
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>
                <b>#</b>
              </TableCell>
              {columns.map((col) => (
                <TableCell key={col.field}>
                  <Box display="flex" alignItems="center">
                    {col.filter && (
                      <Tooltip title="Filter">
                        <IconButton
                          size="small"
                          onClick={(e) => handleFilterClick(e, col)}
                        >
                          <FilterListIcon
                            fontSize="small"
                            color={filters[col.field] ? "primary" : "inherit"}
                          />
                        </IconButton>
                      </Tooltip>
                    )}
                    <b>{col.headerName}</b>
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {data?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center">
                  {loading ? "Loading data..." : "No records found"}
                </TableCell>
              </TableRow>
            ) : (
              data?.map((row, index) => (
                <TableRow key={row._id || index}>
                  <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                  {columns.map((col) => (
                    <TableCell key={col.field}>
                      {col.render ? col.render(row) : row[col.field] ?? "—"}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Filter Popup */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        {activeFilterCol && (
          <Box p={2} width={240}>
            <Stack spacing={1.5}>
              <TextField
                fullWidth
                size="small"
                label={`Filter by ${activeFilterCol}`}
                value={tempFilters[activeFilterCol] || ""}
                onChange={(e) =>
                  handleTempChange(activeFilterCol, e.target.value)
                }
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleClear}
                  color="error"
                >
                  Clear
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleVerify}
                  color="primary"
                >
                  Verify
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}
      </Popover>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={totalRecords}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        disabled={loading}
      />
    </Paper>
  );
}
